import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Sparkle, CheckCircle, X, UploadSimple, ClockCounterClockwise, Megaphone, Trash, LinkSimple, FloppyDisk } from "@phosphor-icons/react";
import AuthorsEditor from "@/components/marketing/AuthorsEditor";

const STATUS_COLORS = {
  "Draft": "bg-slate-100 text-slate-700",
  "In Review / Author": "bg-amber-100 text-amber-800",
  "In Review / Marketing": "bg-sky-100 text-sky-800",
  "On Option": "bg-purple-100 text-purple-800",
  "Published": "bg-emerald-100 text-emerald-800",
  "Rejected": "bg-rose-100 text-rose-800",
  "Active": "bg-blue-100 text-blue-800",
  "Ended": "bg-slate-200 text-slate-600",
};

export default function MarketingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [choices, setChoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [staff, setStaff] = useState([]);
  const [authorsDraft, setAuthorsDraft] = useState([]);

  const email = (user?.email || "").toLowerCase();
  const isAdmin = user?.role === "admin";
  const isGuest = user?.role === "guest";
  const canEdit = isAdmin;   // only admins can edit / approve / reject
  const isReviewer = item?.reviewer_emails?.map((e) => e.toLowerCase()).includes(email);

  // Build the initial authors list — fall back to the legacy single-author
  // fields for items created before multi-author support.
  const deriveAuthors = (it) => {
    if (it?.authors && it.authors.length) return it.authors;
    if (it?.author_name || it?.author_email) {
      return [{ name: it.author_name || "", email: it.author_email || "", department: it.author_department || "" }];
    }
    return [];
  };

  const load = async () => {
    setLoading(true);
    try {
      const [it, ch] = await Promise.all([
        api.get(`/marketing/items/${id}`),
        api.get("/marketing/choices"),
      ]);
      setItem(it.data);
      setChoices(ch.data);
      setAuthorsDraft(deriveAuthors(it.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load item");
      navigate("/marketing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (!canEdit) return;
    api.get("/users").then((r) => setStaff(r.data || [])).catch(() => {});
  }, [canEdit]);

  const runAI = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/items/${id}/ai-review`);
      setItem(data);
      toast.success(`AI score: ${data.ai_insight?.score}/100`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI review failed");
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/items/${id}/approve`, {});
      setItem(data);
      toast.success("Approved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectComment.trim()) return toast.error("Please explain why");
    setBusy(true);
    try {
      const { data } = await api.post(`/marketing/items/${id}/reject`, { comment: rejectComment });
      setItem(data);
      setRejectComment("");
      toast.success("Rejected");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reject");
    } finally { setBusy(false); }
  };

  const patchField = async (patch) => {
    try {
      const { data } = await api.patch(`/marketing/items/${id}`, patch);
      setItem(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const saveAuthors = async () => {
    setBusy(true);
    try {
      const cleaned = authorsDraft.filter((a) => (a.name || "").trim() || (a.email || "").trim());
      const { data } = await api.patch(`/marketing/items/${id}`, { authors: cleaned });
      setItem(data);
      setAuthorsDraft(deriveAuthors(data));
      toast.success("Authors saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save authors");
    } finally { setBusy(false); }
  };

  const deleteItem = async () => {
    setBusy(true);
    try {
      await api.delete(`/marketing/items/${id}`);
      toast.success("Marketing item deleted");
      navigate("/marketing");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete item");
      setBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/marketing/items/${id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItem(data);
      toast.success("Uploaded to OneDrive");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  if (loading || !item) return <p className="text-sm text-slate-500">Loading…</p>;

  const canReviewNow = isAdmin;
  const showApprove = isAdmin && (item.current_status === "In Review / Author" || item.current_status === "In Review / Marketing");
  const authorsChanged = JSON.stringify(authorsDraft) !== JSON.stringify(deriveAuthors(item));

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="marketing-detail">
      <div className="flex items-center justify-between">
        <Link to="/marketing" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><ArrowLeft size={16}/> Back to marketing</Link>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="detail-delete-btn" className="rounded-sm gap-1 border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                  <Trash size={14} /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="detail-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this marketing item?</AlertDialogTitle>
                  <AlertDialogDescription>
                    "{item.title}" will be permanently removed from the tracker. This cannot be undone. Files already uploaded to OneDrive are not deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="detail-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="detail-delete-confirm" disabled={busy} onClick={deleteItem} className="bg-rose-600 hover:bg-rose-700">
                    Delete item
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <span className={`px-3 py-1 rounded-sm text-xs font-medium ${STATUS_COLORS[item.current_status] || "bg-slate-100 text-slate-700"}`} data-testid="detail-status">{item.current_status}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-2">
              <div className="label-tracking inline-flex items-center gap-1"><Megaphone size={12}/></div>
              {!canEdit ? (
                <span className="text-xs text-slate-700 font-medium">{item.classification}</span>
              ) : (
                <Select value={item.classification || "Article"} onValueChange={(v) => patchField({ classification: v })}>
                  <SelectTrigger data-testid="detail-classification" className="h-7 rounded-sm w-40 text-xs">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    {(choices?.classifications || ["Article","Podcast","Campaign","LinkedIn","Video"]).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <InlineText
              testid="detail-title"
              value={item.title || ""}
              placeholder="(untitled — click to add)"
              readOnly={!canEdit}
              onSave={(v) => patchField({ title: v })}
              className="font-display text-3xl text-slate-900 leading-tight"
            />

            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tracking">Authors</div>
                {canEdit && authorsChanged && (
                  <Button onClick={saveAuthors} disabled={busy} size="sm" data-testid="detail-save-authors" className="h-7 rounded-sm gap-1 bg-slate-900 hover:bg-slate-800 text-xs">
                    <FloppyDisk size={12} /> Save authors
                  </Button>
                )}
              </div>
              <AuthorsEditor
                value={authorsDraft}
                onChange={setAuthorsDraft}
                staff={staff}
                readOnly={!canEdit}
                testid="detail-authors"
              />
            </div>
            <div className="text-[11px] text-slate-500">
              Department (primary author): <span className="font-medium text-slate-700">{item.author_department || "—"}</span>
            </div>

            <div>
              <div className="label-tracking mb-1">Description</div>
              <InlineTextarea
                testid="detail-description"
                value={item.description || ""}
                placeholder={!canEdit ? "" : "Click to add a short synopsis…"}
                readOnly={!canEdit}
                onSave={(v) => patchField({ description: v || null })}
              />
            </div>

            <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
              {item.fiscal_quarter && (
                <>Quarter: <span className="font-mono text-slate-700">{item.fiscal_quarter}</span>
                  {item.fiscal_quarter_override && <span className="ml-1 text-[10px] px-1 py-0.5 rounded-sm bg-amber-100 text-amber-800">manual</span>}
                </>
              )}
            </div>
            {canEdit && (
              <QuarterOverridePicker item={item} onSave={patchField} />
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg text-slate-900">Files on OneDrive</h3>
              {canEdit && (
                <label className="text-xs text-blue-600 hover:underline cursor-pointer inline-flex items-center gap-1" data-testid="detail-upload">
                  <UploadSimple size={14}/> Add file
                  <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])}/>
                </label>
              )}
            </div>
            {(item.attachments || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No files uploaded.</p>
            ) : (
              <ul className="space-y-1.5">
                {item.attachments.map((a) => (
                  <li key={a.id} className="text-xs bg-slate-50 border border-slate-200 rounded-sm p-2 flex items-center justify-between gap-2">
                    <span className="truncate">{a.filename} <span className="text-slate-400">({(a.size / 1024).toFixed(0)} KB)</span></span>
                    <div className="flex items-center gap-2">
                      {a.download_url && <a href={a.download_url} className="text-blue-600 hover:underline" target="_blank" rel="noreferrer">Download</a>}
                      {a.onedrive_web_url && <a href={a.onedrive_web_url} className="text-slate-500 hover:text-slate-900" target="_blank" rel="noreferrer">Open in OneDrive</a>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* AI Insights */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg text-slate-900 inline-flex items-center gap-2">
                <Sparkle size={18} weight="fill" className="text-purple-500"/> AI insights
              </h3>
              {canEdit && (
                <Button onClick={runAI} disabled={busy} data-testid="detail-run-ai" size="sm" className="rounded-sm bg-purple-600 hover:bg-purple-700 gap-1">
                  <Sparkle size={12} weight="fill"/> {busy ? "Analyzing…" : (item.ai_insight ? "Re-run" : "Run review")}
                </Button>
              )}
            </div>
            {!item.ai_insight ? (
              <p className="text-sm text-slate-400 italic">No AI review yet.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="text-4xl font-display text-slate-900" data-testid="detail-ai-score">{item.ai_insight.score}</div>
                  <div className="text-xs text-slate-600">/100 · {item.ai_insight.model} · {item.ai_insight.ran_at ? new Date(item.ai_insight.ran_at).toLocaleString() : ""}</div>
                </div>
                {item.ai_insight.insights?.length > 0 && (
                  <div><div className="label-tracking mb-1">Insights</div><ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">{item.ai_insight.insights.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                )}
                {item.ai_insight.unverified_claims?.length > 0 && (
                  <div><div className="label-tracking mb-1 text-amber-700">Unverified claims</div><ul className="text-sm text-amber-900 space-y-1 list-disc pl-5">{item.ai_insight.unverified_claims.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                )}
                {item.ai_insight.factual_issues?.length > 0 && (
                  <div><div className="label-tracking mb-1 text-rose-700">Factual issues</div><ul className="text-sm text-rose-900 space-y-1 list-disc pl-5">{item.ai_insight.factual_issues.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                )}
              </div>
            )}
          </div>

          {/* Review history */}
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-display text-lg text-slate-900 inline-flex items-center gap-2 mb-3">
              <ClockCounterClockwise size={18}/> Review history
            </h3>
            <ol className="space-y-2" data-testid="detail-history">
              {(item.review_history || []).slice().reverse().map((h) => (
                <li key={h.id} className="text-xs border-l-2 border-slate-200 pl-3">
                  <span className="text-slate-500">{new Date(h.created_at).toLocaleString()}</span>
                  {" · "}
                  <span className="font-medium text-slate-900 capitalize">{h.action.replace(/_/g, " ")}</span>
                  {h.actor_name && <span className="text-slate-600"> — {h.actor_name}</span>}
                  {h.comment && <div className="text-slate-700 italic mt-0.5">"{h.comment}"</div>}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <aside className="space-y-4">
          {/* Actions */}
          {showApprove && (
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-2" data-testid="detail-actions">
              <div className="label-tracking">Review actions</div>
              <Button onClick={approve} disabled={busy} data-testid="detail-approve-btn" className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700 gap-1">
                <CheckCircle size={14} weight="fill"/> Approve
              </Button>
              <Textarea placeholder="Reason for rejecting…" value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} rows={2} className="rounded-sm" data-testid="detail-reject-comment"/>
              <Button onClick={reject} disabled={busy || !rejectComment.trim()} data-testid="detail-reject-btn" variant="outline" className="w-full rounded-sm border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700 gap-1">
                <X size={14}/> Reject
              </Button>
            </div>
          )}

          {/* Status pill editor for admins */}
          {isAdmin && choices && (
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-tracking mb-1">Status</div>
              <Select value={item.current_status} onValueChange={(v) => patchField({ current_status: v })}>
                <SelectTrigger data-testid="detail-status-select" className="rounded-sm h-9 text-sm"><SelectValue/></SelectTrigger>
                <SelectContent>{choices.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {/* Publish details (visible in In Review / Marketing) */}
          {canEdit && (
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="detail-publish">
              <div className="label-tracking">Publish details</div>
              <div>
                <Label className="text-[11px] text-slate-500">Received / sent date</Label>
                <Input data-testid="detail-received-at" type="date" defaultValue={item.received_at?.substring(0, 10) || ""} onBlur={(e) => patchField({ received_at: e.target.value || "" })} className="mt-1 rounded-sm h-9 text-sm"/>
                <p className="text-[10px] text-slate-400 mt-0.5">Date the article was sent to / received from the professionals.</p>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Published date</Label>
                <Input data-testid="detail-published-at" type="date" defaultValue={item.published_at?.substring(0, 10) || ""} onBlur={(e) => patchField({ published_at: e.target.value || null })} className="mt-1 rounded-sm h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">LinkedIn posted date</Label>
                <Input data-testid="detail-linkedin-at" type="date" defaultValue={item.linkedin_posted_at?.substring(0, 10) || ""} onBlur={(e) => patchField({ linkedin_posted_at: e.target.value || "" })} className="mt-1 rounded-sm h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Website link</Label>
                <Input data-testid="detail-website-url" type="url" defaultValue={item.website_url || ""} onBlur={(e) => patchField({ website_url: e.target.value || "" })} placeholder="https://barnardinc.co.za/article…" className="mt-1 rounded-sm h-9 text-sm"/>
                {item.website_url && (
                  <a href={item.website_url} target="_blank" rel="noreferrer" data-testid="detail-website-link" className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline break-all">
                    <LinkSimple size={11} /> Open article
                  </a>
                )}
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Campaign end</Label>
                <Input data-testid="detail-campaign-end" type="date" defaultValue={item.campaign_end_at?.substring(0, 10) || ""} onBlur={(e) => patchField({ campaign_end_at: e.target.value || null })} className="mt-1 rounded-sm h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Result</Label>
                <Textarea data-testid="detail-campaign-result" defaultValue={item.campaign_result || ""} rows={2} onBlur={(e) => patchField({ campaign_result: e.target.value || null })} className="mt-1 rounded-sm text-sm"/>
              </div>
            </div>
          )}

          {/* Read-only details for non-admins */}
          {!canEdit && (
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-2" data-testid="detail-readonly-info">
              <div className="label-tracking">Details</div>
              <ReadRow label="Received / sent" value={item.received_at?.substring(0, 10)} />
              <ReadRow label="Published" value={item.published_at?.substring(0, 10)} />
              <ReadRow label="LinkedIn posted" value={item.linkedin_posted_at?.substring(0, 10)} />
              <ReadRow label="Campaign end" value={item.campaign_end_at?.substring(0, 10)} />
              {item.campaign_result && (
                <div><div className="text-[11px] text-slate-500">Result</div><p className="text-sm text-slate-700 whitespace-pre-wrap">{item.campaign_result}</p></div>
              )}
            </div>
          )}

          {/* Reviewers list */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="label-tracking mb-2">Reviewers ({(item.reviewer_emails || []).length})</div>
            {(item.reviewer_emails || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">None assigned.</p>
            ) : (
              <ul className="space-y-1">
                {item.reviewer_emails.map((e) => (
                  <li key={e} className="text-xs font-mono text-slate-700 bg-slate-50 rounded-sm px-2 py-1">{e}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Links */}
          {(item.website_url || (item.links || []).length > 0) && (
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="label-tracking mb-2">Links</div>
              <ul className="space-y-1">
                {item.website_url && (
                  <li><a href={item.website_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all inline-flex items-center gap-1"><LinkSimple size={11}/> Article on website</a></li>
                )}
                {(item.links || []).map((l, i) => (
                  <li key={i}><a href={l.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline break-all">{l.label || l.url}</a></li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </motion.div>
  );
}

// Builds a rolling 8-quarter window (4 past + current + 3 future) from the
// current fiscal-year config on the server so admins never have to type.
function useQuarterOptions() {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/general");
        const fyMonth = data.fiscal_year_start_month || 3;
        const fyDay = data.fiscal_year_start_day || 1;
        const today = new Date();
        // Find current fiscal year start
        let fyStart = new Date(today.getFullYear(), fyMonth - 1, fyDay);
        if (fyStart > today) fyStart = new Date(today.getFullYear() - 1, fyMonth - 1, fyDay);
        const monthsIn = (today.getFullYear() - fyStart.getFullYear()) * 12 + (today.getMonth() - fyStart.getMonth());
        const curQ = Math.min(3, Math.floor(monthsIn / 3));
        const list = [];
        for (let offset = -4; offset <= 3; offset += 1) {
          let qIndex = curQ + offset;
          let yearOffset = 0;
          while (qIndex < 0) { qIndex += 4; yearOffset -= 1; }
          while (qIndex > 3) { qIndex -= 4; yearOffset += 1; }
          const fy = fyStart.getFullYear() + yearOffset;
          list.push(`FY${fy}-Q${qIndex + 1}`);
        }
        if (!cancelled) setOptions(list);
      } catch (e) {
        console.debug("quarter options fetch failed", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return options;
}

function QuarterOverridePicker({ item, onSave }) {
  const options = useQuarterOptions();
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(item.fiscal_quarter_override || "");
  const submit = async (v) => {
    setValue(v);
    setSaving(true);
    try {
      await onSave({ fiscal_quarter_override: v || "" });
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-4 flex items-center gap-2 text-xs">
      <Label className="text-slate-600">Fiscal quarter</Label>
      <Select value={value || "__auto__"} onValueChange={(v) => submit(v === "__auto__" ? "" : v)}>
        <SelectTrigger data-testid="quarter-override" className="h-8 rounded-sm w-52 text-xs font-mono">
          <SelectValue placeholder="Auto (from submission date)"/>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__auto__">Auto — {item.fiscal_quarter || "unknown"} (from submission)</SelectItem>
          {options.map((q) => (
            <SelectItem key={q} value={q}>{q}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {saving && <span className="text-slate-400">Saving…</span>}
    </div>
  );
}


function ReadRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="text-slate-700 font-medium">{value || "—"}</span>
    </div>
  );
}

// -------- Inline-editable primitives --------
// Design: fields look plain when idle, sprout a subtle border on hover, and
// autosave on blur if the value actually changed. `readOnly` renders as
// non-interactive text so guests get the same layout without the input chrome.

function InlineText({ value, onSave, placeholder = "", readOnly = false, className = "", testid }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  if (readOnly) {
    return <div data-testid={testid} className={className}>{value || <span className="text-slate-400 italic">{placeholder}</span>}</div>;
  }
  const commit = () => { if (draft !== value) onSave?.(draft); };
  return (
    <input
      data-testid={testid}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      placeholder={placeholder}
      className={`${className} w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-slate-900 rounded-sm px-1 -mx-1 outline-none transition-colors`}
    />
  );
}

function InlineLabelled({ label, value, onSave, readOnly = false, testid }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { if (draft !== value) onSave?.(draft); };
  return (
    <div>
      <div className="label-tracking mb-1">{label}</div>
      {readOnly ? (
        <div className="text-sm text-slate-700 font-medium">{value || <span className="text-slate-400">—</span>}</div>
      ) : (
        <input
          data-testid={testid}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
          className="w-full text-sm text-slate-900 bg-transparent border border-slate-200 hover:border-slate-400 focus:border-slate-900 rounded-sm px-2 py-1 outline-none transition-colors"
        />
      )}
    </div>
  );
}

function InlineTextarea({ value, onSave, placeholder = "", readOnly = false, testid }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { if (draft !== value) onSave?.(draft); };
  if (readOnly) {
    return <p className="text-sm text-slate-700 whitespace-pre-wrap">{value || <span className="text-slate-400 italic">{placeholder || "—"}</span>}</p>;
  }
  return (
    <Textarea
      data-testid={testid}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      placeholder={placeholder}
      rows={4}
      className="w-full rounded-sm text-sm bg-transparent border-slate-200 hover:border-slate-400 focus:border-slate-900 transition-colors"
    />
  );
}

